import { Check, Crosshair, Pause, Play, RotateCcw, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";

type TrailPoint = { x: number; y: number; time: string; label?: string; verified?: boolean };

const TRAIL: TrailPoint[] = [
  { x: 42, y: 205, time: "22:04", label: "START" },
  { x: 73, y: 178, time: "22:08" },
  { x: 112, y: 164, time: "22:13", label: "GATE A", verified: true },
  { x: 151, y: 181, time: "22:17" },
  { x: 184, y: 143, time: "22:22", label: "LOADING", verified: true },
  { x: 217, y: 116, time: "22:27" },
  { x: 258, y: 128, time: "22:31", label: "NORTH", verified: true },
  { x: 290, y: 87, time: "22:36", label: "ACTIVE" },
];

export function PatrolTrailDemo({ detailed = false }: { detailed?: boolean }) {
  const [step, setStep] = useState(1);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (!playing) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setStep(TRAIL.length);
      setPlaying(false);
      return;
    }
    const timer = window.setInterval(() => {
      setStep((current) => current >= TRAIL.length ? 1 : current + 1);
    }, detailed ? 950 : 800);
    return () => window.clearInterval(timer);
  }, [detailed, playing]);

  const shown = TRAIL.slice(0, step);
  const current = shown[shown.length - 1] ?? TRAIL[0];
  const path = useMemo(
    () => shown.map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`).join(" "),
    [shown],
  );

  const restart = () => { setStep(1); setPlaying(true); };

  return (
    <div className={`mr-patrol-demo ${detailed ? "is-detailed" : ""}`} aria-label="Interactive live patrol breadcrumb demonstration">
      <div className="mr-demo-toolbar">
        <span><i /> LIVE PATROL</span>
        <span><ShieldCheck /> GPS VERIFIED <Check /></span>
      </div>
      <div className="mr-patrol-map">
        <div className="mr-map-street street-a" /><div className="mr-map-street street-b" /><div className="mr-map-street street-c" />
        <span className="mr-map-zone zone-a">WAREHOUSE A</span><span className="mr-map-zone zone-b">NORTH GATE</span>
        {/* role="group", not role="img": the breadcrumb points inside are real
            controls, and an image role must not contain focusable children. */}
        <svg viewBox="20 55 300 175" role="group" aria-label={`Patrol trail with ${shown.length} timestamped locations`}>
          <path className="mr-patrol-path-shadow" d={path} />
          <path className="mr-patrol-path" d={path} />
          {shown.map((point, index) => (
            <g
              key={`${point.time}-${index}`}
              className="mr-patrol-point"
              onClick={() => { setStep(index + 1); setPlaying(false); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setStep(index + 1);
                  setPlaying(false);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Show patrol location ${point.label ?? point.time} at ${point.time}`}
            >
              <circle cx={point.x} cy={point.y} r={point.verified ? 7 : 4} className={point.verified ? "is-checkpoint" : ""} />
              {point.verified && <path d={`M${point.x - 3} ${point.y}l2 2 4-5`} />}
              {(point.label || detailed) && <text x={point.x + 9} y={point.y - 3}>{point.label ?? point.time}</text>}
              {point.label && <text className="mr-map-time" x={point.x + 9} y={point.y + 8}>{point.time}</text>}
            </g>
          ))}
          <g className="mr-officer-marker" transform={`translate(${current.x} ${current.y})`}>
            <circle r="13" /><circle r="5" /><path d="M0-8v-6M0 8v6M-8 0h-6M8 0h6" />
          </g>
        </svg>
        <div className="mr-map-readout"><Crosshair /><span>OFFICER S. TORRES</span><b>{current.time}</b></div>
      </div>
      <div className="mr-patrol-controls">
        <Button type="button" variant="outline" size="icon" onClick={() => setPlaying((value) => !value)} aria-label={playing ? "Pause patrol replay" : "Play patrol replay"}>
          {playing ? <Pause /> : <Play />}
        </Button>
        <div><span>ROUTE PROGRESS</span><strong>{Math.round((step / TRAIL.length) * 100)}%</strong><i><b style={{ width: `${(step / TRAIL.length) * 100}%` }} /></i></div>
        <Button type="button" variant="outline" size="icon" onClick={restart} aria-label="Restart patrol replay"><RotateCcw /></Button>
      </div>
      <div className="mr-demo-sequence"><span className={step >= 1 ? "is-active" : ""}>OFFICER MOVES</span><span className={step >= 3 ? "is-active" : ""}>LOCATION UPDATES</span><span className={step >= 5 ? "is-active" : ""}>TRAIL BUILDS</span><span className={step >= 7 ? "is-active" : ""}>ACTIVITY VERIFIED</span></div>
    </div>
  );
}